/**
 * Phase 27.36 — Sentry browser monitoring (autonomie pre-launch 2026-06-01).
 *
 * Strict Loi 25 / RGPD friendly :
 *  - No session replay (replaysSessionSampleRate 0)
 *  - No replay on error (replaysOnErrorSampleRate 0)
 *  - No perf tracing (tracesSampleRate 0)
 *  - sendDefaultPii false (no IP, no headers auto)
 *  - beforeSend scrubs user PII + request body
 *  - beforeBreadcrumb drops /api/lead body (form data with PII)
 *
 * Enabled only when VITE_SENTRY_DSN is set. Defaults to silent no-op.
 * Error monitoring est considéré "intérêt légitime" Loi 25 (debug + sécurité),
 * donc activé par défaut SANS consent gate — à condition de scrub PII strict.
 */
// Phase 27.42 — switch @sentry/react → @sentry/browser (~10 KB gzip économisé).
// On n'utilise PAS les features React-spécifiques (Sentry.ErrorBoundary, Profiler,
// withErrorBoundary). On utilise juste Sentry.init + captureException qui sont
// identiques dans @sentry/browser. Le pipe vers notre <ErrorBoundary> custom
// reste via reportErrorBoundaryError ci-dessous (componentStack inclus en context).
import * as Sentry from '@sentry/browser';
import type { ErrorInfo } from 'react';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (!dsn || dsn === 'REPLACE_AT_END') return;

  // Phase 27.40 — utiliser les defaults Sentry SDK v10 (globalHandlersIntegration,
  // breadcrumbsIntegration, etc.) au lieu de [] qui désactiverait window.onerror
  // + unhandledrejection capture. Replay/Tracing sont opt-in explicite — pas
  // inclus par défaut, donc l'hygiène Loi 25 reste intacte (tracesSampleRate +
  // replaysSessionSampleRate à 0 = defense in depth si futur upgrade SDK les
  // bouge en default).
  Sentry.init({
    dsn,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? 'unknown',
    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
      }
      if (event.request?.data) {
        event.request.data = '[Filtered Loi 25 — body redacted client-side]';
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      const url = breadcrumb.data?.url as string | undefined;
      if (breadcrumb.category === 'fetch' && url?.includes('/api/lead')) {
        return null;
      }
      return breadcrumb;
    }
  });
  initialized = true;
}

export function reportErrorBoundaryError(error: Error, info: ErrorInfo): void {
  if (!initialized) return;
  Sentry.captureException(error, {
    contexts: {
      react: { componentStack: info.componentStack ?? '' }
    }
  });
}

export function isSentryInitialized(): boolean {
  return initialized;
}
